import { faArrowRight, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { List, ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { BackendSearchType, GroupedSearchResults, SearchResult, SearchType } from 'src/models/search/_types';
import {
  getSearchResultPath,
  getSearchTypeLabel,
  isBackendSearchType,
  isExternalUrl,
  search,
} from 'src/models/search/search';
import { useReactPlanCreatorEnabled } from 'src/pages/Creator/hooks/useReactPlanCreatorEnabled';

import { useNavigationSearch } from './useNavigationSearch';


export interface GlobalSearchDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Search types to include */
  types?: SearchType[];
  /** Optional callback when a result is selected. If returns true, prevents default navigation */
  onSelect?: (result: SearchResult, type: SearchType) => boolean | void;
}

const DEFAULT_TYPES: SearchType[] = ['navigation', 'report', 'clients', 'group', 'package', 'plans', 'product', 'service', 'exercise', 'gym'];

// Create debounced function outside component to avoid recreating
const createDebouncedSearch = (setter: (value: string) => void) =>
  debounce((value: string) => {
    setter(value);
  }, 300);

export const GlobalSearchDialog: React.FC<GlobalSearchDialogProps> = ({
  open,
  onClose,
  types = DEFAULT_TYPES,
  onSelect,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'globalSearch' });
  const navigate = useNavigate();
  const reactCreatorEnabled = useReactPlanCreatorEnabled();
  const inputRef = useRef<HTMLInputElement>(null);
  const { searchNavigation } = useNavigationSearch();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Separate backend types from frontend types
  const backendTypes = types.filter(isBackendSearchType) as BackendSearchType[];
  const hasNavigationType = types.includes('navigation');

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchValue('');
      setDebouncedSearchValue('');
    }
  }, [open]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Debounced search value update
  const debouncedSetSearchRef = useRef(createDebouncedSearch(setDebouncedSearchValue));

  // Cleanup debounce on unmount
  useEffect(() => {
    const debouncedFn = debouncedSetSearchRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSetSearchRef.current(value);
  };

  // Backend API search (only for backend types)
  const { data: backendResults, isFetching } = useQuery({
    queryKey: ['globalSearch', debouncedSearchValue, backendTypes],
    queryFn: () =>
      search({
        term: debouncedSearchValue,
        types: backendTypes,
        limit: 5,
        manageOnly: true,
      }),
    enabled: debouncedSearchValue.length >= 2 && backendTypes.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  // Frontend navigation search
  const navigationResults =
    hasNavigationType && debouncedSearchValue.length >= 2
      ? searchNavigation(debouncedSearchValue)
      : [];

  // Combine results with navigation first
  const combinedResults: GroupedSearchResults = {};
  if (hasNavigationType && navigationResults.length > 0) {
    combinedResults['navigation'] = navigationResults;
  }
  if (backendResults) {
    Object.assign(combinedResults, backendResults);
  }

  // Check if a type has any exact/near-exact matches (name includes search term)
  const hasExactMatch = (type: SearchType): boolean => {
    const results = combinedResults[type] || [];
    const lowerTerm = debouncedSearchValue.toLowerCase();
    return results.some(
      (r) =>
        r.name?.toLowerCase().includes(lowerTerm) ||
        r.aliases?.some((a) => a.toLowerCase().includes(lowerTerm))
    );
  };

  // Sort types by: 1) has exact matches first, 2) then by result count
  const orderedTypes: SearchType[] = [
    // Navigation first if it has results
    ...(hasNavigationType && navigationResults.length > 0 ? ['navigation' as SearchType] : []),
    // Then backend types sorted by exact matches first, then by result count
    ...backendTypes
      .filter((type) => combinedResults[type]?.length > 0)
      .sort((a, b) => {
        const aHasExact = hasExactMatch(a);
        const bHasExact = hasExactMatch(b);
        // Prioritize types with exact matches
        if (aHasExact && !bHasExact) return -1;
        if (!aHasExact && bHasExact) return 1;
        // Then sort by number of results
        return (combinedResults[b]?.length || 0) - (combinedResults[a]?.length || 0);
      }),
  ];

  // Flatten results for keyboard navigation
  const flattenedResults: { result: SearchResult; type: SearchType }[] = [];
  orderedTypes.forEach((type) => {
    const typeResults = combinedResults[type] || [];
    typeResults.forEach((result) => {
      flattenedResults.push({ result, type });
    });
  });

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedSearchValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (flattenedResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < flattenedResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (flattenedResults[selectedIndex]) {
          const { result, type } = flattenedResults[selectedIndex];
          handleResultClick(result, type);
        }
        break;
    }
  };

  const handleResultClick = (result: SearchResult, type: SearchType) => {
    // Call onSelect callback if provided
    if (onSelect) {
      const preventDefault = onSelect(result, type);
      if (preventDefault) {
        onClose();
        return;
      }
    }

    // Handle navigation type
    if (type === 'navigation') {
      const route = result.data?.route as string;
      const isExternal = result.data?.isExternal as boolean;
      if (route) {
        if (isExternal) {
          window.open(route, '_blank');
        } else {
          navigate(route);
        }
        onClose();
      }
      return;
    }

    // Default navigation behavior for backend types
    const path = getSearchResultPath(type, result, reactCreatorEnabled);
    if (path) {
      if (isExternalUrl(path)) {
        window.open(path, '_blank');
      } else {
        navigate(path);
      }
      onClose();
    }
  };

  const hasResults = Object.values(combinedResults).some((r) => r.length > 0);
  const showNoResults =
    debouncedSearchValue.length >= 2 && !isFetching && !hasResults;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      subtitle={t('subtitle')}
      maxWidth="sm"
      fullWidth
    >
      <TextField
        inputRef={inputRef}
        value={searchValue}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        placeholder={t('placeholder')}
        fullWidth
        autoFocus
        sx={{ mt: 0.1 }}
        startAdornment={
          <Icon
            size="small"
            color="action"
            sx={{ pr: 0.5 }}
            icon={faMagnifyingGlass}
          />
        }
        endAdornment={
          isFetching ? <CircularProgress size="small" color="inherit" /> : null
        }
      />
      <Box sx={{ mt: 2, maxHeight: 400, overflowY: 'auto' }}>
        {showNoResults && (
          <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
            {t('noResults')}
          </Typography>
        )}

        {hasResults && (
          <SearchResults
            results={combinedResults}
            types={orderedTypes}
            onResultClick={handleResultClick}
            selectedIndex={selectedIndex}
          />
        )}
      </Box>
    </Dialog>
  );
};

interface SearchResultsProps {
  results: GroupedSearchResults;
  types: SearchType[];
  onResultClick: (result: SearchResult, type: SearchType) => void;
  selectedIndex: number;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  types,
  onResultClick,
  selectedIndex,
}) => {
  // Track the global index across all results
  let globalIndex = 0;

  return (
    <Stack spacing={2}>
      {types.map((type) => {
        const typeResults = results[type];
        if (!typeResults || typeResults.length === 0) {
          return null;
        }

        return (
          <Box key={type}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                px: 1,
                py: 0.5,
                display: 'block',
                position: 'sticky',
                top: 0,
                bgcolor: 'background.paper',
                zIndex: 1,
              }}
            >
              {getSearchTypeLabel(type)}
            </Typography>
            <List disablePadding>
              {typeResults.map((result) => {
                const currentIndex = globalIndex;
                globalIndex++;
                return (
                  <SearchResultItem
                    key={`${type}-${result.id}`}
                    result={result}
                    type={type}
                    onClick={() => onResultClick(result, type)}
                    isSelected={currentIndex === selectedIndex}
                  />
                );
              })}
            </List>
          </Box>
        );
      })}
    </Stack>
  );
};

interface SearchResultItemProps {
  result: SearchResult;
  type: SearchType;
  onClick: () => void;
  isSelected?: boolean;
}

const SearchResultItem: React.FC<SearchResultItemProps> = ({
  result,
  type,
  onClick,
  isSelected,
}) => {
  const itemRef = useRef<HTMLButtonElement>(null);
  const isNavigation = type === 'navigation';

  // Scroll selected item into view
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);

  // For navigation items, don't show aliases (they're just the ID)
  const secondary =
    !isNavigation && result.aliases && result.aliases.length > 0
      ? result.aliases[0]
      : undefined;

  if (isNavigation) {
    return (
      <ListItem
        ref={itemRef}
        clickable
        onClick={onClick}
        icon={faArrowRight}
        primary={result.name}
        rounded
        selected={isSelected}
      />
    );
  }

  return (
    <ListItem
      ref={itemRef}
      clickable
      onClick={onClick}
      avatar={<Avatar src={result.profileImage} size="small" />}
      primary={result.name}
      secondary={secondary}
      rounded
      selected={isSelected}
    />
  );
};

/**
 * Search trigger button component for use in navigation
 */
export interface GlobalSearchTriggerProps {
  onClick: () => void;
}

export const GlobalSearchTrigger: React.FC<GlobalSearchTriggerProps> = ({
  onClick,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'globalSearch' });

  return (
    <Tooltip title={t('title')}>
      <IconButton onClick={onClick}>
        <Icon
          color="action"
          size="medium"
          icon={faMagnifyingGlass}
        />
      </IconButton>
    </Tooltip>
  );
};
